import { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  Accordion,
  InputField,
  Select,
  SelectOption,
  Tooltip,
  Toggle,
  IconName,
} from '@ets/design-system';

import styles from './Section.module.scss';
import { ColorPickerToggle, MeasureInput } from '_common/components';
import { useEffectOnUpdate } from '_common/hooks';

type StyleProps = Pick<Editor.Edition.TableProperties, 'background' | 'border'> & SectionProps;

const DEFAULT_PROPERTIES: Pick<Editor.Edition.TableProperties, 'background'> & {
  borderColor: string;
  borderWidth: number;
  borderStyle: Editor.Data.Node.BorderStyle;
  borderChosen: Editor.Edition.BorderChosen;
} = {
  background: 'transparent',
  borderColor: '#000000',
  borderWidth: 0.75,
  borderStyle: 's',
  borderChosen: 'none',
};

type ToggleOption = {
  value: Editor.Edition.BorderChosen;
  icon: IconName;
  label: string;
};

const BORDER_TOGGLES: ToggleOption[] = [
  {
    value: 'all',
    icon: 'border-all',
    label: 'ALL_BORDERS',
  },
  {
    value: 'top',
    icon: 'border-top',
    label: 'TOP_BORDER',
  },
  {
    value: 'right',
    icon: 'border-right',
    label: 'RIGHT_BORDER',
  },
  {
    value: 'bottom',
    icon: 'border-bottom',
    label: 'BOTTOM_BORDER',
  },
  {
    value: 'left',
    icon: 'border-left',
    label: 'LEFT_BORDER',
  },
  {
    value: 'inside',
    icon: 'border-inner',
    label: 'INSIDE_BORDERS',
  },
  {
    value: 'outside',
    icon: 'border-outer',
    label: 'OUTSIDE_BORDERS',
  },
  {
    value: 'none',
    icon: 'border-none',
    label: 'NO_BORDERS',
  },
];

type BorderStyleOption = {
  value: Editor.Data.Node.BorderStyle;
  label: string;
};

//Forcefully restricting value type while maintaining SelectOption type
const BORDER_STYLES: (SelectOption & BorderStyleOption)[] = [
  { value: 's', label: '\u2014 (solid)' },
  { value: 'dot', label: '\u00B7\u00B7\u00B7 (dotted)' },
  { value: 'das', label: '--- (dashed)' },
  { value: 'd', label: '\u2550 \u2550 \u2550 (double)' },
  { value: 'nil', label: 'NONE' },
];

const Style = ({ background, border, updateData }: StyleProps) => {
  const intl = useIntl();

  const borderStyleOptions = useMemo(
    () =>
      BORDER_STYLES.map((borderStyle) => ({
        ...borderStyle,
        label: intl.formatMessage({ id: borderStyle.label }),
      })),
    [],
  );

  const [localBackground, setLocalBackground] = useState(
    background ?? DEFAULT_PROPERTIES.background ?? 'transparent',
  );
  useEffectOnUpdate(() => {
    updateData({
      property: 'background',
      value: localBackground,
    });
  }, [localBackground]);

  const [localBorderColor, setLocalBorderColor] = useState(
    border?.color?.value ?? DEFAULT_PROPERTIES.borderColor ?? 'transparent',
  );
  useEffectOnUpdate(() => {
    updateData({
      property: 'border',
      value: {
        ...border,
        color: { value: localBorderColor },
      },
    });
  }, [localBorderColor]);

  const [localBorderWidth, setLocalBorderWidth] = useState(
    `${border?.width?.value ?? DEFAULT_PROPERTIES.borderWidth}`,
  );
  const [localBorderStyle, setLocalBorderStyle] = useState(
    borderStyleOptions.find(
      (borderStyleOption) =>
        borderStyleOption.value === (border?.style?.value ?? DEFAULT_PROPERTIES.borderStyle),
    ),
  );
  const [localBorderChosen, setLocalBorderChosen] = useState(border?.chosen?.value);

  //#region Property change handlers
  const handleBorderStyleChange = (newStyle: (typeof borderStyleOptions)[number] | null) => {
    if (!newStyle) return;
    setLocalBorderStyle(newStyle);
    updateData({ property: 'border', value: { ...border, style: { value: newStyle.value } } });
  };

  const handleBorderWidthChange = (newValue: string) => {
    setLocalBorderWidth(newValue);
    updateData({ property: 'border', value: { ...border, width: { value: +newValue } } });
  };

  const handleChosenBorderChange = (newChosenBorder: Editor.Edition.BorderChosen) => {
    setLocalBorderChosen(newChosenBorder);

    //Relates to requirement.4 of "Allow borderless in table/cell limits"
    if (
      newChosenBorder !== 'none' &&
      localBorderWidth === '0' &&
      localBorderStyle?.value === 'nil' &&
      localBorderColor === ''
    ) {
      setLocalBorderWidth(`${DEFAULT_PROPERTIES.borderWidth}`);
      setLocalBorderStyle(borderStyleOptions.find((option) => option.value === 's'));
      setLocalBorderColor(DEFAULT_PROPERTIES.borderColor);

      updateData({
        property: 'border',
        value: {
          ...border,
          width: { value: DEFAULT_PROPERTIES.borderWidth },
          style: { value: 's' },
          chosen: { value: newChosenBorder },
        },
      });
    } else {
      updateData({
        property: 'border',
        value: { ...border, chosen: { value: newChosenBorder } },
      });
    }
  };
  //#endregion

  return (
    <Accordion
      size="m"
      variant="standard"
      title={intl.formatMessage({ id: 'STYLE' })}
      defaultOpen={true}
      testId="tableProperties-style"
    >
      <div className={styles.root}>
        <div className={styles.column}>
          <InputField
            size="m"
            label={intl.formatMessage({ id: 'BACKGROUND_COLOR' })}
            testId="tableProperties-background-color"
          >
            <ColorPickerToggle
              color={localBackground}
              onColorChange={setLocalBackground}
              testId="tableProperties-background-color"
            />
          </InputField>
        </div>
        <div className={styles.column}>
          <InputField
            size="m"
            label={intl.formatMessage({ id: 'BORDER' })}
            testId="tableProperties-border"
          >
            <Select
              variant="standard"
              size="m"
              value={
                localBorderChosen === 'none'
                  ? borderStyleOptions.find((option) => option.value === 'nil')
                  : localBorderStyle
              }
              options={borderStyleOptions}
              onChange={handleBorderStyleChange}
              menuPosition="fixed"
              testId="border-styles"
              disabled={localBorderChosen === 'none'}
            />
            <div
              className={`${styles.row} ${styles.withMargin}`}
              style={{ height: '4rem', marginBottom: '3rem' }}
            >
              <MeasureInput
                size="m"
                value={localBorderChosen === 'none' ? '0' : localBorderWidth}
                valueSuffix="pt"
                placeholder=""
                onChange={handleBorderWidthChange}
                avoidEmpty
                testId="tableProperties-border"
                disabled={localBorderChosen === 'none'}
              />
              <ColorPickerToggle
                color={localBorderColor}
                onColorChange={setLocalBorderColor}
                testId="tableProperties-border-color"
              />
            </div>
            <div className={styles.shelf}>
              {BORDER_TOGGLES.map((toggle) => (
                <Tooltip
                  key={`borderType-${toggle.value}`}
                  content={intl.formatMessage({ id: toggle.label })}
                  testId={`tableProperties-border-${toggle.value}`}
                >
                  <span>
                    <Toggle
                      size="m"
                      variant="link"
                      isToggled={localBorderChosen === toggle.value}
                      iconProps={{ icon: toggle.icon }}
                      onClick={() => handleChosenBorderChange(toggle.value)}
                      testId={`tableProperties-border-${toggle.value}`}
                    />
                  </span>
                </Tooltip>
              ))}
            </div>
          </InputField>
        </div>
      </div>
    </Accordion>
  );
};

export default Style;
